import { useEffect, useRef, useState } from "react";
import GridLayout, {
  type LayoutItem,
  horizontalCompactor,
  verticalCompactor,
  useContainerWidth,
} from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import RemoveIcon from "../../../assets/icons/remove_icon.svg";
import DragIcon from "../../../assets/icons/drag_icon.svg";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useDroppable,
} from "@dnd-kit/core";
import AddWidget from "./add-widget";
import "./dashboard-drag.scss";
import WidgetLibraryDrawer from "./widget-library-drawer";
import WidgetPreview from "./widget-preview";
import { WidgetConfig, WidgetLibraryItem } from "../../../utils/util-types";
import { routes } from "../../../constants/routes";
import { useNavigate } from "react-router-dom";

export const COLS = 3;

export function reflowLayoutByColumnsForBlackCardOnly(
  existing: LayoutItem[],
  blanks: LayoutItem[],
  cols: number
): LayoutItem[] {
  // 1. Initialize column heights using existing layout
  const colHeights = Array(cols).fill(0);

  existing.forEach((item) => {
    for (let i = item.x; i < item.x + item.w; i++) {
      colHeights[i] = Math.max(colHeights[i], item.y + item.h);
    }
  });

  // 2. Reflow ONLY blank items
  const reflowedBlanks = blanks.map((item) => {
    let bestX = 0;
    let bestY = Infinity;

    for (let x = 0; x <= cols - item.w; x++) {
      const y = Math.max(...colHeights.slice(x, x + item.w));

      if (y < bestY) {
        bestY = y;
        bestX = x;
      }
    }

    const next: LayoutItem = {
      ...item,
      x: bestX,
      y: bestY,
    };

    for (let i = bestX; i < bestX + item.w; i++) {
      colHeights[i] = bestY + item.h;
    }

    return next;
  });

  return [...existing, ...reflowedBlanks];
}

// export function reflowLayoutByColumns(
//   layout: LayoutItem[],
//   cols: number
// ): LayoutItem[] {
//   // Track current Y height of each column
//   const colHeights = Array(cols).fill(0);

//   return layout.map((item) => {
//     // Find the best column range where item fits
//     let bestX = 0;
//     let bestY = Infinity;

//     for (let x = 0; x <= cols - item.w; x++) {
//       const y = Math.max(...colHeights.slice(x, x + item.w));

//       if (y < bestY) {
//         bestY = y;
//         bestX = x;
//       }
//     }

//     // Place item
//     const next: LayoutItem = {
//       ...item,
//       x: bestX,
//       y: bestY,
//     };

//     // Update column heights
//     for (let i = bestX; i < bestX + item.w; i++) {
//       colHeights[i] = bestY + item.h;
//     }
//     return next;
//   });
// }
export function compactLayoutAfterDelete(
  layout: LayoutItem[],
  cols: number
): LayoutItem[] {
  // 1. Stable order (DO NOT trust y after delete)
  const items = [...layout].map((item) => ({
    ...item,
    w: item.w ?? 1,
    h: item.h ?? 1,
  }));

  const colHeights = Array(cols).fill(0);

  const result: LayoutItem[] = [];

  for (const item of items) {
    let bestX = 0;
    let bestY = Number.MAX_SAFE_INTEGER;

    // 2. Find lowest possible placement
    for (let x = 0; x <= cols - item.w; x++) {
      const y = Math.max(...colHeights.slice(x, x + item.w));

      if (y < bestY) {
        bestY = y;
        bestX = x;
      }
    }

    const placed: LayoutItem = {
      ...item,
      x: bestX,
      y: bestY,
    };

    // 3. Update skyline CORRECTLY
    for (let i = bestX; i < bestX + item.w; i++) {
      colHeights[i] = bestY + item.h;
    }

    result.push(placed);
  }

  return result;
}

interface DashboardDragInterface {
  editMode: boolean;
  setRemoveItem: (value: LayoutItem) => void;
  setAddedItem: (
    value: WidgetConfig,
    widgetLibraryItem: WidgetLibraryItem | null,
    index: number,
    newId: string,
    oldId: string
  ) => void;
  childItems: WidgetConfig[];
  layoutItems: LayoutItem[];
  widgetLibraryItemList: WidgetLibraryItem[];
  setChildItems: (value: WidgetConfig[]) => void;
  setLayoutItems: React.Dispatch<React.SetStateAction<LayoutItem[]>>;
  getChildren: (code?: string) => any;
  setLayoutUpdated: (value: boolean) => void;
}
export default function DashboardDrag(props: DashboardDragInterface) {
  const {
    editMode,
    setRemoveItem,
    setAddedItem,
    childItems,
    layoutItems,
    widgetLibraryItemList,
    setChildItems,
    setLayoutItems,
    getChildren,
    setLayoutUpdated,
  } = props;
  const navigate = useNavigate();
  const { width, containerRef, mounted } = useContainerWidth();
  const [activeItemId, setActiveItemId] = useState<string | number>("");
  const [disableAnim, setDisableAnim] = useState(false);
  const prevLayoutRef = useRef<LayoutItem[]>([]);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [activeWidget, setActiveWidget] = useState<WidgetLibraryItem | null>(
    null
  );
  const [draggingItem, setDraggingItem] = useState<string | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveWidget(event.active.data.current as WidgetLibraryItem);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    for (let index = 0; index < childItems.length; index++) {
      if (childItems[index]?.id === over?.id) {
        const time = new Date().getTime();
        const widget: WidgetLibraryItem = active.data
          .current as WidgetLibraryItem;
        const newId = `widget-${time}-${activeWidget?.id}`;

        const newAddedItem = {
          id: newId,
          dashboardWidgetId: "",
          widgetId: activeWidget?.id || "",
          code: activeWidget?.code,
          child: getChildren(activeWidget?.code),
          isDroppable: false,
          title: activeWidget?.name,
          isNoteCRM: activeWidget?.code === "NOTES_TO_CRM",
          isScanner: activeWidget?.code === "BUSINESS_CARD_SCANNER",
        };
        //update addedItem
        setAddedItem(
          newAddedItem,
          activeWidget,
          index,
          newId,
          childItems[index]?.id
        );
        setActiveWidget(null);
        break;
      }
    }
  };

  const onAddClick = (libraryItem: WidgetLibraryItem) => {
    let childIndex = 0;
    for (let index = 0; index < childItems.length; index++) {
      if (childItems[index]?.isDroppable) {
        childIndex = index;
        break;
      }
    }

    const time = new Date().getTime();

    const newId = `widget-${time}-${libraryItem?.id}`;

    const newAddedItem = {
      id: newId,
      dashboardWidgetId: "",
      widgetId: libraryItem?.id || "",
      code: libraryItem?.code,
      child: getChildren(libraryItem?.code),
      isDroppable: false,
      title: libraryItem?.name,
      isNoteCRM: libraryItem?.code === "NOTES_TO_CRM",
      isScanner: libraryItem?.code === "BUSINESS_CARD_SCANNER",
    };

    //update addedItem
    setAddedItem(
      newAddedItem,
      libraryItem,
      childIndex,
      newId,
      childItems[childIndex]?.id
    );
  };

  const getLayoutItemToAddFromLibrary = (itemId: string) => {
    return { i: itemId, x: 2, y: 2, w: 1, h: 1, isResizable: false };
  };

  const getChildItemToFromLibrary = (itemId: string) => {
    const moreChildItems = {
      id: itemId,
      isDroppable: true,
      dashboardWidgetId: "",
      widgetId: "",
      title: "",
      isNoteCRM: false,
      isScanner: false,
      code: "",
      child: (
        <AddWidget id={itemId} onAddWidgetClick={() => setDrawerOpen(true)} />
      ),
    };
    return moreChildItems;
  };

  const enableGridAnimation = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setDisableAnim(false);
      });
    });
  };

  function handleRemove(removeItem: LayoutItem) {
    setDisableAnim(true);
    setRemoveItem(removeItem);

    const itemId = `widget-${new Date().getTime()}`;
    const childItem = getChildItemToFromLibrary(itemId);
    const widgetItem = getLayoutItemToAddFromLibrary(itemId);

    const newChildItems = childItems.filter(
      (item: WidgetConfig) => item.id !== removeItem.i
    );

    newChildItems.push(childItem);
    setChildItems(newChildItems);

    const remaining = layoutItems.filter((item) => item.i !== removeItem?.i);
    remaining.push(widgetItem);
    setLayoutItems(compactLayoutAfterDelete(remaining, COLS));

    enableGridAnimation();
  }
  function onRemoveClick(removeItem: WidgetLibraryItem) {
    setDisableAnim(true);

    const itemId = `widget-${new Date().getTime()}`;
    const childItem = getChildItemToFromLibrary(itemId);
    const widgetItem = getLayoutItemToAddFromLibrary(itemId);

    const removedChildItem = childItems.filter(
      (item: WidgetConfig) => item.widgetId === removeItem.id
    );

    const newChildItems = childItems.filter(
      (item: WidgetConfig) => item.widgetId !== removeItem.id
    );

    newChildItems.push(childItem);
    setChildItems(newChildItems);

    const removedLayoutItem: LayoutItem[] = layoutItems.filter(
      (item: LayoutItem) => item.i === removedChildItem[0].id
    );
    setRemoveItem(removedLayoutItem[0]);

    const remaining = layoutItems.filter(
      (item) => item.i !== removedChildItem[0].id
    );
    remaining.push(widgetItem);
    setLayoutItems(compactLayoutAfterDelete(remaining, COLS));

    enableGridAnimation();
  }

  const addBlankWidget = () => {
    let size = layoutItems?.length || 0;
    let startIndex = layoutItems?.length || 0;
    const blankItemCount = (widgetLibraryItemList?.length || 0) - size;

    const blankChildItemList = [];
    const blankLayoutItemList: LayoutItem[] = [];
    for (let index = 0; index < blankItemCount; index++) {
      const itemId = `widget-${new Date().getTime()}-${startIndex}}`;
      blankChildItemList.push(getChildItemToFromLibrary(itemId));
      blankLayoutItemList.push(getLayoutItemToAddFromLibrary(itemId));
      startIndex = startIndex + 1;
    }

    setChildItems([...childItems, ...blankChildItemList]);

    setLayoutItems((prev: LayoutItem[]) =>
      reflowLayoutByColumnsForBlackCardOnly(prev, blankLayoutItemList, COLS)
    );
  };
  const removeBlankWidget = () => {
    const newLayoutItems: LayoutItem[] = [];
    const newChildItems: any[] = [];
    const editTableItemIds: string[] = [];
    childItems.forEach((item: WidgetConfig) => {
      if (item.isDroppable) {
        editTableItemIds.push(item.id);
      }
    });

    childItems.forEach((item) => {
      if (!editTableItemIds.includes(item.id)) {
        newChildItems.push(item);
      }
    });
    layoutItems.forEach((item) => {
      if (!editTableItemIds.includes(item.i)) {
        newLayoutItems.push(item);
      }
    });
    setChildItems(newChildItems);
    setLayoutItems(newLayoutItems);
  };
  useEffect(() => {
    setDisableAnim(true);
    if (editMode && widgetLibraryItemList?.length > 0) {
      addBlankWidget();
    } else {
      removeBlankWidget();
    }
    enableGridAnimation();
  }, [editMode]);

  return (
    // onDragStart={handleDragStart} onDragEnd={handleDragEnd}
    <DndContext>
      <WidgetLibraryDrawer
        isOpen={isDrawerOpen}
        widgetLibraryItemList={widgetLibraryItemList}
        closeDrawer={() => {
          setDrawerOpen(false);
        }}
        onRemoveClick={onRemoveClick}
        onAddClick={onAddClick}
      />
      <div
        ref={containerRef}
        style={{ width: "100%" }}
        onMouseDown={(e) => {
          if (!(e.target as HTMLElement).classList.contains("drag-handle")) {
            e.stopPropagation();
          }
        }}
      >
        {mounted && (
          <GridLayout
            key={`GridLayout-${childItems.length}-${layoutItems.length}`}
            className={disableAnim ? "no-animation" : ""}
            layout={layoutItems}
            width={width}
            gridConfig={{
              cols: 3,
              rowHeight: 213,
              margin: [10, 10],
              containerPadding: [10, 10],
            }}
            compactor={horizontalCompactor}
            onDragStart={(item: any, e: any) => {
              const target = e?.target as HTMLElement | undefined;
              if (!target?.classList.contains("drag-handle")) {
                return false; // do nothing
              }
              setDraggingItem(item.i);
              prevLayoutRef.current = layoutItems.map((item) => ({ ...item }));
            }}
            onDragStop={(newLayout, oldItem, newItem) => {
              const compacted = verticalCompactor.compact(newLayout, COLS);
              setLayoutItems([...compacted]);
              setLayoutUpdated(true);
              //setLayoutItems(normalizeLayout([...compacted]));
            }}
            onLayoutChange={(l: any) => {
              setLayoutItems(l);
            }}
          >
            {layoutItems.map((layoutItem: any, index: number) => (
              <div
                key={layoutItem.i}
                className={`drag-item-section-cards ${!childItems[index]?.isDroppable ? "child-padding" : ""}`}
              >
                <div className="drag-item-section-cards-heading">
                  {!childItems[index]?.isDroppable && (
                    <>
                      <div
                        className="non-draggable-div"
                        onMouseDownCapture={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        <div className="title-container">
                          <span className="card-title">
                            {childItems[index]?.title || ""}
                          </span>
                          {childItems[index]?.isNoteCRM && (
                            <div
                              onClick={() => {
                                navigate(routes.GENERAL_NOTES);
                              }}
                              className="view-all"
                            >
                              View All
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        className={`draggable-div ${childItems[index]?.isScanner ? "primary-color-bg" : childItems[index]?.isNoteCRM ? "note-header-bg" : "white-bg"}`}
                      >
                        <div
                          className="remove-parent"
                          onMouseDownCapture={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                        >
                          {editMode && (
                            <button
                              type="button"
                              className="action-icon-parent cursor-pointer remove-icon"
                              onMouseDownCapture={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              onClick={() => handleRemove(layoutItem)}
                            >
                              <img
                                src={RemoveIcon}
                                alt="remove"
                                draggable={false}
                                className="action-icon"
                              />
                            </button>
                          )}
                        </div>

                        {editMode && (
                          <button
                            type="button"
                            className="drag-handle action-icon-parent cursor-move"
                          >
                            <img
                              className="action-icon"
                              src={DragIcon}
                              alt="drag"
                            />
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div
                  className="child-container"
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseDownCapture={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  {childItems[index]?.child}
                </div>
              </div>
            ))}
          </GridLayout>
        )}

        {!editMode && layoutItems?.length === 0 && (
          <div className="no-widgets">No widgets available</div>
        )}
        {/* {activeWidget && (
          <DragOverlay style={{ zIndex: 2000 }}>
            {activeWidget ? <WidgetPreview widget={activeWidget} /> : null}
          </DragOverlay>
        )} */}
      </div>
    </DndContext>
  );
}
